import { configureStore } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import languageReducer from './slices/languageSlice';

const PERSIST_KEY = 'ezts_auth';
/** Tab-only user snapshot for rememberMe=false (survives F5, not tab close). */
const SESSION_PERSIST_KEY = 'ezts_auth_session';

/** Foreign / legacy keys this app never reads (admin leftover, old logins). */
const UNUSED_STORAGE_KEYS = ['auth-storage', 'token', 'auth'];

function clearUnusedStorageKeys() {
  if (typeof window === 'undefined') return;
  try {
    UNUSED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

function removeItem(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

function writeAuthSnapshot(storage, key, payload) {
  storage.setItem(key, JSON.stringify(payload));
}

function clearAuthSnapshots() {
  if (typeof window === 'undefined') return;
  removeItem(localStorage, PERSIST_KEY);
  removeItem(sessionStorage, SESSION_PERSIST_KEY);
}

clearUnusedStorageKeys();

const persistMiddleware = (store) => (next) => (action) => {
  const result = next(action);
  if (action.type.startsWith('auth/')) {
    const { isAuthenticated, user, rememberMe } = store.getState().auth;

    try {
      if (!isAuthenticated || !user) {
        clearAuthSnapshots();
        return result;
      }

      const snapshot = {
        isAuthenticated: true,
        user,
        rememberMe: Boolean(rememberMe),
      };

      if (rememberMe) {
        // Long session: persist user only — never accessToken.
        writeAuthSnapshot(localStorage, PERSIST_KEY, snapshot);
        removeItem(sessionStorage, SESSION_PERSIST_KEY);
      } else {
        // Short session: tab memory so F5 keeps the page alive.
        writeAuthSnapshot(sessionStorage, SESSION_PERSIST_KEY, snapshot);
        removeItem(localStorage, PERSIST_KEY);
      }
    } catch {
      // ignore
    }
  }
  return result;
};

const readPersistedAuth = () => {
  try {
    if (typeof window === 'undefined') return undefined;

    const longRaw = localStorage.getItem(PERSIST_KEY);
    if (longRaw) {
      const parsed = JSON.parse(longRaw);
      if (parsed?.user && parsed.rememberMe && parsed.isAuthenticated) {
        removeItem(sessionStorage, SESSION_PERSIST_KEY);
        return {
          auth: {
            isAuthenticated: true,
            user: parsed.user,
            rememberMe: true,
            loading: false,
            error: null,
          },
        };
      }
      removeItem(localStorage, PERSIST_KEY);
    }

    const shortRaw = sessionStorage.getItem(SESSION_PERSIST_KEY);
    if (shortRaw) {
      const parsed = JSON.parse(shortRaw);
      if (parsed?.user && parsed.isAuthenticated && !parsed.rememberMe) {
        return {
          auth: {
            isAuthenticated: true,
            user: parsed.user,
            rememberMe: false,
            loading: false,
            error: null,
          },
        };
      }
      removeItem(sessionStorage, SESSION_PERSIST_KEY);
    }

    return undefined;
  } catch {
    clearAuthSnapshots();
    return undefined;
  }
};

const persisted = readPersistedAuth();

export const store = configureStore({
  preloadedState: persisted,
  reducer: {
    auth: authReducer,
    language: languageReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(persistMiddleware),
});
